import React from "react";
//#region types
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModelFinancialReport } from "wint-api-client/dist/types";
import { IconDefinitionConstants } from "utils/constants";
import { Amount } from "models/utilTypes/currencyType";
import { StyleEnums } from "models/enums";
import { ReportHanlder, ReportProps } from "models/propTypes/accountingPropTypes";
import { NestedTableHandles } from "models/propTypes/simpleTableProps";
//#endregion
//#region components
import { MonthRangeSelectorBubble, DrillDownTable, useDownload } from "components/common";
import useFinancialReportRow from "../hooks/useFinancialReportRow";
import useApplyExpandAllRows from "../hooks/useApplyExpandAllRows";
import useStandardReportHelpers from "../hooks/useStandardReportHelpers";
import useTransactionAmount from "../hooks/useTransactionAmount";
import PrintStandardResultReport from "./print/standard";
import { useFinancialReportService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance, DateHelper, ExportHelper, FinancialReportHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const StandardIncomeStatement: React.ForwardRefRenderFunction<ReportHanlder, ReportProps> = (
  { updateReportExportData, reloadKey, reload },
  ref
) => {
  const financialReportService = useFinancialReportService();
  const {
    result,
    filter,
    primaryColumnQuickSelections,
    comparisonColumnQuickSelections,
    onRepeatColumnFilterChange,
    onRepeatColumnDelete,
    getRange,
    getSelection,
    loading,
    pageFilter,
  } = useStandardReportHelpers(financialReportService.getResultReport);

  const { handleDownloadPDFRender } = useDownload();

  const tableRef = React.useRef<NestedTableHandles>(null);

  React.useImperativeHandle(ref, () => ({
    print: (title?: string, subtitle?: string, fileName?: string) => {
      handleDownloadPDFRender(
        <PrintStandardResultReport
          title={title}
          subtitle={subtitle}
          result={result}
          amountFormatter={amountFormatter}
          differenceFormatter={differenceFormatter}
          getRange={getRange}
          amountHeaderDescription={amountHeaderDescription}
          showAccountNumber={pageFilter?.showAccountNumber ?? false}
          hideAverageMonthAndDifference={pageFilter?.hideAverageMonthAndDifference ?? false}
        />,
        `${fileName}.pdf`
      );
    },
    expandAll: () => tableRef.current?.expandAll(),
    collapseAll: () => tableRef.current?.collapseAll(),
  }));

  const amountHeaderDescription = (repeatIndex: number) =>
    repeatIndex === 0 ? TranslationKeys.Common.AverageMonth : TranslationKeys.Common.Difference;

  const getRowAmount = (row: WintSuperkollAPIModelFinancialReport.FinancialReportRowColumn) =>
    row.amount ? row.amount * -1 : row.amount;
  const amountFormatter = (row: WintSuperkollAPIModelFinancialReport.FinancialReportRowColumn) =>
    new Amount(undefined, getRowAmount(row));

  const differenceFormatter = (
    row: WintSuperkollAPIModelFinancialReport.FinancialReportRowColumn,
    rowIndex?: number,
    depth?: number,
    repeatIndex?: number,
    parentRow?: any
  ) => {
    let value = row.difference ? row.difference * -1 : row.difference;
    if (repeatIndex === 0) {
      const range = getRange(repeatIndex);
      const months = range?.start && range?.end ? DateHelper.getDiffMonths(range.start, range.end) : 0;
      value = months ? getRowAmount(row) / months : 0;
    }
    return new Amount(undefined, value);
  };

  const { descriptionFormatter } = useFinancialReportRow(pageFilter?.showAccountNumber ?? false);

  React.useEffect(() => {
    const hideAverageMonthAndDifference = pageFilter?.hideAverageMonthAndDifference ?? false;

    const headers = [
      [
        "",
        ...(filter?.columns ?? []).flatMap((c) => {
          const startMonth = c.range?.start == null ? "" : DateHelper.formatDateToNumericString(c.range?.start);
          const endMonth = c.range?.end == null ? "" : DateHelper.formatDateToNumericString(c.range?.end);
          return hideAverageMonthAndDifference ? [`${startMonth} - ${endMonth}`] : [`${startMonth} - ${endMonth}`, ""];
        }),
      ],
      ...ExportHelper.generateSheetHeaders(result, (_, index) =>
        hideAverageMonthAndDifference
          ? [LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Amount)]
          : [
              LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Amount),
              LocalizationHelperInstance.getFormattedMessage(amountHeaderDescription(index)),
            ]
      ),
    ];

    const sheet = ExportHelper.generateSheetData(result, descriptionFormatter, (col, index) =>
      hideAverageMonthAndDifference
        ? [amountFormatter(col).amount]
        : [amountFormatter(col).amount, differenceFormatter(col, undefined, undefined, index).amount]
    );
    updateReportExportData(headers.concat(sheet));
  }, [result, pageFilter, descriptionFormatter]);

  React.useEffect(() => {
    reload();
  }, [pageFilter?.showAccountNumber]);

  useApplyExpandAllRows(tableRef, result, pageFilter?.expandAllRows);

  const { transactionAmountFormatter } = useTransactionAmount(filter, true);

  return (
    <DrillDownTable<
      WintSuperkollAPIModelFinancialReport.FinancialReportRow,
      WintSuperkollAPIModelFinancialReport.FinancialReportRowColumn
    >
      key={reloadKey}
      tableRef={tableRef}
      rows={result}
      rowTextProperty={{
        key: "description",
        properties: {
          description: TranslationKeys.Common.Name,
          formatter: descriptionFormatter,
        },
      }}
      repeatProperties={{
        key: "columns",
        properties: {
          amount: {
            description: TranslationKeys.Common.Amount,
            formatter: transactionAmountFormatter,
            alignment: "right",
          },
          ...(pageFilter?.hideAverageMonthAndDifference
            ? {}
            : {
                difference: {
                  description: amountHeaderDescription,
                  formatter: differenceFormatter,
                  alignment: "right",
                },
              }),
        },
        sumNegative: (row: WintSuperkollAPIModelFinancialReport.FinancialReportRowColumn) => row.amount > 0,
        filterField: (repeatIndex: number) => (
          <MonthRangeSelectorBubble
            label=""
            onChange={onRepeatColumnFilterChange.bind(null, repeatIndex)}
            range={getRange(repeatIndex)}
            selection={getSelection(repeatIndex)}
            displayType={repeatIndex > 0 ? StyleEnums.DisplayType.Info : StyleEnums.DisplayType.Success}
            popperPlacement="bottom"
            quickSelections={repeatIndex > 0 ? comparisonColumnQuickSelections : primaryColumnQuickSelections}
            actions={
              repeatIndex > 0
                ? [
                    {
                      text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.RemoveColumn),
                      icon: IconDefinitionConstants.WTrashAlt,
                      onClickHandler: onRepeatColumnDelete.bind(null, repeatIndex),
                    },
                  ]
                : undefined
            }
          />
        ),
        addRepeatColumnField: (repeatIndex: number) => (
          <MonthRangeSelectorBubble
            label=""
            onChange={onRepeatColumnFilterChange.bind(null, repeatIndex)}
            popperPlacement="left-start"
            icon={IconDefinitionConstants.WPlus}
            className={cssExports.addNew}
            quickSelections={comparisonColumnQuickSelections}
          />
        ),
      }}
      childrenKey="children"
      rowDisplayType={FinancialReportHelper.getResultReportRowDisplayType}
      loading={loading}
    />
  );
};

export default React.forwardRef(StandardIncomeStatement);
